import { Link2 } from 'lucide-react';
import type { OrganizationMemberDto } from '@sara/api-client';
import { Button } from '@sara/ui/components/button';
import { DataPanel } from '@sara/ui/components/data-panel';

interface PendingInviteListProps {
  invites: OrganizationMemberDto[];
  canManage: boolean;
  onCopyLink: () => void;
  onRevoke: (invite: OrganizationMemberDto) => void;
}

function formatDate(value: string | Date | null | undefined) {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const COLUMNS = 'grid-cols-[1.6fr_6rem_1.2fr_12rem]';
const COLUMN_TEMPLATE = '1.6fr 6rem 1.2fr 12rem';

export function PendingInviteList({
  invites,
  canManage,
  onCopyLink,
  onRevoke,
}: PendingInviteListProps) {
  return (
    <DataPanel>
      <DataPanel.Header columns={COLUMN_TEMPLATE}>
        <span>Email</span>
        <span>Role</span>
        <span>Invited</span>
        <span className="sr-only">Actions</span>
      </DataPanel.Header>
      <DataPanel.Body>
        <ul className="divide-y divide-border/60">
          {invites.map((invite) => (
            <li
              key={invite.id}
              data-testid="pending-invite-row"
              className={`grid ${COLUMNS} items-center gap-6 px-6 py-5`}
            >
              <span className="truncate text-[15px] font-medium tracking-tight text-foreground">
                {invite.email}
              </span>
              <span className="text-[13px] text-muted-foreground">
                {invite.role === 'ADMIN' ? 'Admin' : 'Member'}
              </span>
              <span className="truncate text-[13px] tabular-nums text-muted-foreground">
                {formatDate(invite.createdAt)}
                {invite.invitedBy && ` · by ${invite.invitedBy}`}
              </span>
              <div className="flex shrink-0 items-center gap-2 justify-self-end">
                <Button
                  variant="ghost"
                  shape="pill"
                  size="sm"
                  className="gap-1.5 text-muted-foreground hover:text-foreground"
                  onClick={onCopyLink}
                  data-testid="copy-invite-link"
                >
                  <Link2 className="h-3.5 w-3.5" strokeWidth={1.6} aria-hidden />
                  Copy link
                </Button>
                {canManage && (
                  <Button
                    variant="outline"
                    shape="pill"
                    size="sm"
                    onClick={() => onRevoke(invite)}
                    data-testid="revoke-invite"
                  >
                    Revoke
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </DataPanel.Body>
      <DataPanel.Footer>
        <span className="tabular-nums">
          {invites.length} pending {invites.length === 1 ? 'invite' : 'invites'}
        </span>
      </DataPanel.Footer>
    </DataPanel>
  );
}
